<h1 mat-dialog-title>
  <ng-container [ngSwitch]="context">
    <ng-container *ngSwitchCase="'option'">{{ 'runGearDialog.header.option' | translate }}</ng-container>
    <ng-container *ngSwitchCase="'data'">{{ 'runGearDialog.header.utility' | translate }}</ng-container>
    <ng-container *ngSwitchCase="'analysis'">{{ 'runGearDialog.header.analysis' | translate }}</ng-container>
    <ng-container *ngSwitchDefault>{{ 'runGearDialog.header.other' | translate }}</ng-container>
  </ng-container>
</h1>

<button mat-icon-button mat-dialog-close test-id="close-button" class="mat-dialog-close-button">
  <mat-icon>close</mat-icon>
</button>

<div mat-dialog-content>
  <gear-version-selector [isLoading]="isLoading" [gears]="gears" [(context)]="context"
    [formControl]="forms.gearSelection">
  </gear-version-selector>

  <mat-tab-group *ngIf="selectedGearId && context !== 'option'" animationDuration="0ms" mat-stretch-tabs="false" class="tabs-compact tabs-white">
    <mat-tab>
      <ng-template matTabLabel>
        <span test-id="tab-label-inputs">{{ 'runGearDialog.inputs.tabLabel' | translate }}</span>
        <mat-icon *ngIf="hasInputErrors" class="flag-icon xs">flag</mat-icon>
      </ng-template>
      <ng-template matTabContent>
        <div class="tab-content">
          <!-- API key warning -->
          <div *ngIf="apiKeyInput" test-id="api-key-warning" class="api-key-warning">
            <mat-icon class="xs">warning</mat-icon>
            <span>{{ 'runGearDialog.inputs.apiKeyWarning' | translate }}</span>
          </div>
          <!-- File inputs -->
          <div *ngIf="!isBatch" test-id="gear-inputs" class="gear-input-form">
            <div class="info">
              <strong class="title">{{ 'runGearDialog.inputs.label' | translate }}</strong>
            </div>
            <div *ngIf="fileInputs.length === 0" class="no-inputs">{{ 'runGearDialog.inputs.noFileInputs' | translate }}</div>
            <div *ngFor="let input of fileInputs; trackBy: getInputKey" [attr.test-id]="'gear-input-' + input.key" class="gear-input">
              <div class="gear-input-info">
                <span>{{ input.key }}</span>
                <mat-icon *ngIf="input.description" class="xs help-icon" [matTooltip]="input.description"
                  matTooltipPosition="left" matTooltipClass="tooltip-multiline">info</mat-icon>
              </div>
              <div class="gear-input-value">
                <mat-form-field appearance="outline" class="full-width">
                  <input type="text" [attr.test-id]="'gear-input-field-' + input.key" matInput readonly
                    [formControl]="$any(forms.inputs.get(input.key)).get('label')" (click)="selectGearInput(input.key)"
                    [placeholder]="(input.optional ? 'runGearDialog.inputs.noInputOptional' : 'runGearDialog.inputs.noInputRequired') | translate"
                    matTooltip="{{ 'runGearDialog.inputs.inputTooltip' | translate }}" matTooltipPosition="left">
                  <button *ngIf="input.optional && forms.inputs.get(input.key).get('label').value"
                    [attr.test-id]="'gear-input-clear-' + input.key" matSuffix mat-icon-button
                    (click)="clearGearInput(input.key)" matTooltip="{{ 'common.labels.clearTooltip' | translate }}" matTooltipPosition="right">
                    <mat-icon>close</mat-icon>
                  </button>
                  <mat-error>{{ 'runGearDialog.validation.inputs.required' | translate }}</mat-error>
                </mat-form-field>
              </div>
            </div>
          </div>
          <!-- Context inputs -->
          <ng-container *ngIf="contextInputs">
            <div *ngFor="let input of contextInputs | keyvalue" test-id="context-input" class="gear-context-input">
              <ng-container *ngIf="input.value.found">
                <mat-icon class="found xs help-icon">info</mat-icon>
                <span [innerHTML]="'runGearDialog.inputs.contextInputFound' | translate | interpolate:input.key:input.value.container_type:input.value.label | trusthtml"></span>
              </ng-container>
              <ng-container *ngIf="!input.value.found">
                <mat-icon class="missing xs">warning</mat-icon>
                <span [innerHTML]="'runGearDialog.inputs.contextInputMissing' | translate | interpolate:input.key | trusthtml"></span>
              </ng-container>
            </div>
          </ng-container>
          <!-- Batch info/inputs -->
          <div *ngIf="isBatch" class="gear-batch">
            <p>{{ 'runGearDialog.batchInfo.fileUsage' | translate }}</p>
            <p>{{ 'runGearDialog.batchInfo.jobRequirements' | translate }}</p>
            <ng-container *ngIf="requiredFileInputs.length > 0">
              <strong class="title">{{ 'runGearDialog.batchInfo.requiredFiles' | translate }}</strong>
              <ul>
                <li *ngFor="let input of requiredFileInputs; trackBy:getInputKey" test-id="batch-required-input">
                  <strong>{{ input.key }}</strong> - {{ input.description }}
                </li>
              </ul>
            </ng-container>
            <ng-container *ngIf="optionalFileInputs.length > 0">
              <strong class="title">{{ 'runGearDialog.batchInfo.optionalFiles' | translate }}</strong>
              <mat-form-field appearance="outline" class="full-width">
                <mat-select [formControl]="forms.optionalInputPolicy" test-id="optional-input-policy"
                  [placeholder]="'runGearDialog.optionalInputPolicy.placeholder' | translate" class="no-max-width">
                  <mat-option value="ignored">{{ 'runGearDialog.optionalInputPolicy.ignored' | translate }}</mat-option>
                  <mat-option value="flexible">{{ 'runGearDialog.optionalInputPolicy.flexible' | translate }}</mat-option>
                  <mat-option value="required">{{ 'runGearDialog.optionalInputPolicy.required' | translate }}</mat-option>
                </mat-select>
              </mat-form-field>
              <ul>
                <li *ngFor="let input of optionalFileInputs; trackBy:getInputKey" test-id="batch-optional-input">
                  <strong>{{ input.key }}</strong> - {{ input.description }}
                </li>
              </ul>
            </ng-container>
          </div>
          <!-- Output location -->
          <div *ngIf="forms.outputContainer.enabled" class="gear-output">
            <div class="gear-output-info info">
              <strong class="title">{{ 'runGearDialog.outputLocation.label' | translate }}</strong>
              <mat-icon class="xs help-icon" matTooltip="{{ 'runGearDialog.outputLocation.info' | translate }}" matTooltipPosition="left">info</mat-icon>
            </div>
            <mat-form-field appearance="outline" class="full-width">
              <input type="text" test-id="output-location-field" matInput readonly [formControl]="$any(forms.outputContainer.get('label'))"
                (click)="selectOutputLocation()" placeholder="{{ 'runGearDialog.outputLocation.placeholder' | translate }}"
                matTooltip="{{ 'runGearDialog.outputLocation.tooltip' | translate }}" matTooltipPosition="left">
                <mat-error>{{ 'runGearDialog.validation.outputLocation.required' | translate }}</mat-error>
            </mat-form-field>
          </div>
          <!-- Analysis label -->
          <div *ngIf="context === 'analysis'" class="analysis-label">
            <div class="analysis-label-info info">
              <strong class="title">{{ 'runGearDialog.analysisLabel.label' | translate }}</strong>
              <mat-icon class="xs help-icon" matTooltip="{{ 'runGearDialog.analysisLabel.info' | translate }}" matTooltipPosition="left">info</mat-icon>
            </div>
            <mat-form-field appearance="outline" class="full-width">
              <input type="text" test-id="analysis-label-field" matInput [formControl]="forms.analysisLabel">
              <mat-error *ngIf="forms.analysisLabel.errors?.required">{{ 'runGearDialog.validation.analysisLabel.required' | translate }}</mat-error>
              <mat-error *ngIf="forms.analysisLabel.errors?.minlength as minlength">
                {{ 'runGearDialog.validation.analysisLabel.minlength' | translate | interpolate:minlength.requiredLength }}
              </mat-error>
              <mat-error *ngIf="forms.analysisLabel.errors?.maxlength as maxlength">
                {{ 'runGearDialog.validation.analysisLabel.maxlength' | translate | interpolate:maxlength.requiredLength }}
              </mat-error>
            </mat-form-field>
          </div>
          <div *ngIf="jobPrioritiesEnabled" class="job-priority">
            <div class="info">
              <strong class="title">{{ 'runGearDialog.priority.title' | translate }}</strong>
              <span>{{ 'runGearDialog.priority.meta' | translate }}</span>
            </div>
            <mat-form-field appearance="outline">
              <mat-select test-id="job-priority-select" [formControl]="forms.priority">
                <mat-option *ngFor="let option of priorities" [value]="option">
                  {{ 'jobs.priority.'+option | translate }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          <!-- Job tags -->
          <div class="job-tags">
            <div class="job-tags-info info">
              <strong class="title">{{ 'runGearDialog.jobTags.label' | translate }}</strong>
              <mat-icon class="xs help-icon" matTooltip="{{ 'runGearDialog.jobTags.info' | translate }}" matTooltipPosition="right">info</mat-icon>
            </div>
            <mat-form-field class="full-width no-float-space">
              <fw-chip-input [formControl]="forms.tags" test-id="job-tags-field" placeholder="{{ 'runGearDialog.jobTags.placeholder' | translate }}"></fw-chip-input>
            </mat-form-field>
          </div>
        </div>
      </ng-template>
    </mat-tab>
    <mat-tab>
      <ng-template matTabLabel>
        <span test-id="tab-label-configuration">{{ 'runGearDialog.configuration.tabLabel' | translate }}</span>
        <mat-icon *ngIf="gearConfig?.hasErrors || false" class="flag-icon xs">flag</mat-icon>
      </ng-template>
      <gear-config #gearConfig [config]="gears[selectedGearId].gear.config" [formControl]="forms.config" class="tab-content"></gear-config>
    </mat-tab>
    <mat-tab>
      <ng-template matTabLabel>
        <span test-id="tab-label-information">{{ 'runGearDialog.information.tabLabel' | translate }}</span>
      </ng-template>
      <ng-template matTabContent>
        <gear-info [gear]="gears[selectedGearId]" class="tab-content"></gear-info>
      </ng-template>
    </mat-tab>
  </mat-tab-group>
</div>

<footer *ngIf="context !== 'option'" mat-dialog-actions>
  <fw-button
    color="slate"
    *ngIf="hasContextOptions"
    test-id="back-button"
    variant="outline"
    leftIcon="arrow-back"
    (click)="context = 'option'">
    {{ 'common.actions.back' | translate }}
  </fw-button>
  <div class="right-buttons">
    <fw-button
      color="slate"
      variant="outline"
      mat-dialog-close
      test-id="close-button">
      {{ 'common.actions.close' | translate }}
    </fw-button>
    <fw-button
      test-id="run-button"
      [disabled]="isSaving || !(isValid$ | async)"
      (click)="runGear()">
      {{ 'runGearDialog.buttons.run' | translate }}
    </fw-button>
  </div>
</footer>
